import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './components/AdminLayout';
import SettingsManager from './components/SettingsManager';
import ContentManager from './components/ContentManager';
import TestimonialManager from './components/TestimonialManager';
import ProjectManager from './components/ProjectManager';
import BlogManager from './components/BlogManager';
import ResumeManager from './components/ResumeManager';
import PublicationManager from './components/PublicationManager';
import Icon from '../../components/AppIcon';

const AdminDashboard = () => {
    const { tab } = useParams();
    const navigate = useNavigate();
    
    // Default to 'settings' if no tab provided or matched
    const activeTab = tab || 'settings';
    
    const [adminKey, setAdminKey] = useState(localStorage.getItem('admin_key') || '');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [loginError, setLoginError] = useState(null);

    const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

    const verifyKey = async (key) => {
        setIsVerifying(true);
        setLoginError(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/verify`, {
                headers: { 'X-Admin-Key': key }
            });
            if (res.ok) {
                setIsAuthenticated(true);
                setAdminKey(key);
                localStorage.setItem('admin_key', key);
            } else {
                setLoginError('Invalid secret key. Access denied.');
                localStorage.removeItem('admin_key');
                setIsAuthenticated(false);
            }
        } catch (err) {
            setLoginError('Verification failed. Please check your connection.');
        } finally {
            setIsVerifying(false);
        }
    };

    useEffect(() => {
        if (adminKey && !isAuthenticated) {
            verifyKey(adminKey);
        }
    }, []);

    const handleTabChange = (newTab) => {
        navigate(`/admin/${newTab}`);
    };

    const handleLogin = (e) => {
        e.preventDefault();
        const key = e.target.adminKey.value;
        if (key) {
            verifyKey(key);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('admin_key');
        setAdminKey('');
        setIsAuthenticated(false);
        setLoginError(null);
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-surface/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Icon name="Lock" size={32} className="text-accent" />
                        </div>
                        <h1 className="font-heading font-bold text-2xl text-text-primary">Admin Access</h1>
                        <p className="text-text-secondary mt-2">Enter your secure key to access the dashboard</p>
                    </div>
                    
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">Secret Key</label>
                            <input
                                name="adminKey"
                                type="password"
                                required
                                disabled={isVerifying}
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent outline-none transition-smooth disabled:opacity-50"
                                placeholder="••••••••••••"
                            />
                            {loginError && (
                                <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                                    <Icon name="AlertCircle" size={14} />
                                    {loginError}
                                </p>
                            )}
                            <p className="text-[10px] text-text-secondary mt-2 text-center italic opacity-60">
                                Enter your assigned secure Admin Key.
                            </p>
                        </div>
                        <button
                            type="submit"
                            disabled={isVerifying}
                            className="w-full bg-accent hover:bg-accent/90 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-smooth shadow-lg shadow-accent/25 flex items-center justify-center gap-2"
                        >
                            {isVerifying && (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            )}
                            {isVerifying ? 'Verifying...' : 'Log In'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    const renderContent = () => {
        switch (activeTab) {
            case 'settings': return <SettingsManager adminKey={adminKey} />;
            case 'skills': return <ContentManager type="skills" adminKey={adminKey} />;
            case 'achievements': return <ContentManager type="achievements" adminKey={adminKey} />;
            case 'testimonials': return <TestimonialManager adminKey={adminKey} />;
            case 'projects': return <ProjectManager adminKey={adminKey} />;
            case 'blog': return <BlogManager adminKey={adminKey} />;
            case 'resume': return <ResumeManager adminKey={adminKey} />;
            case 'research': return <PublicationManager adminKey={adminKey} />;
            default: return <SettingsManager adminKey={adminKey} />;
        }
    };

    return (
        <AdminLayout activeTab={activeTab} setActiveTab={handleTabChange}>
            <div className="flex justify-between items-center mb-10">
                <div>
                  <h1 className="text-3xl font-heading font-bold text-text-primary">
                    {activeTab.charAt(0).toUpperCase() + activeTab.slice(1).replace('_', ' ')}
                  </h1>
                  <p className="text-text-secondary mt-1">Manage your dynamic home page content</p>
                </div>
                <button 
                  onClick={handleLogout}
                  className="px-4 py-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-smooth flex items-center gap-2 text-sm font-medium"
                >
                  <Icon name="LogOut" size={16} />
                  Logout
                </button>
            </div>
            {renderContent()}
        </AdminLayout>
    );
};

export default AdminDashboard;
